import { IconBackspace } from "@tabler/icons-react";
import React from "react";
import { KeyboardCharacterKey } from "./KeyboardCharacterKey";
import { KeyboardKey } from "./KeyboardKey";
import { KeyboardLine } from "./KeyboardLine";
import classNames from "classnames";

export declare namespace AprilFoolsKeyboard {
  export interface Props {
    onPressCharacter: ((char: string) => void) | undefined;
    onPressBackspace: (() => void) | undefined;
    onPressEnter: (() => void) | undefined;
  }
}

const DEFAULT_TEXT_COLOR = "text-gray-300";

// April Fools 2026 keyboard layout with weird symbols
const APRIL_FOOLS_LAYOUT = [
  ["◕", "‿", "◔", "ಠ", "ಥ", "ʘ", "ಸ", "ಿ", "ಡ", "ಇ"],
  ["(", ")", "¯", "\\", "_", "/", "ツ", "♪"],
  ["ENTER", "ಠ", "ಿ", "ಡ", "ಇ", "ಸ", "ಥ", "BACKSPACE"]
];

export const AprilFoolsKeyboard = React.memo(function AprilFoolsKeyboardImpl({
  onPressBackspace,
  onPressCharacter,
  onPressEnter,
}: AprilFoolsKeyboard.Props) {
  const renderCharacter = (character: string) => {
    return (
      <KeyboardCharacterKey
        className={classNames(getBackgroundColor(), DEFAULT_TEXT_COLOR)}
        character={character}
        onClick={
          onPressCharacter
            ? () => {
              onPressCharacter(character);
            }
            : undefined
        }
      />
    );
  };

  const renderKey = (key: string) => {
    if (key === "ENTER") {
      return (
        <KeyboardKey
          containerClassName={classNames(DEFAULT_TEXT_COLOR, "basis-24")}
          className={getBackgroundColor()}
          text="ENTER"
          onClick={onPressEnter}
        />
      );
    } else if (key === "BACKSPACE") {
      return (
        <KeyboardKey
          containerClassName={classNames(DEFAULT_TEXT_COLOR, "basis-14")}
          className={getBackgroundColor()}
          text={<IconBackspace size={32} />}
          onClick={onPressBackspace}
        />
      );
    } else {
      return renderCharacter(key);
    }
  };

  return (
    <div className="flex flex-col min-w-0">
      {APRIL_FOOLS_LAYOUT.map((row, rowIndex) => (
        <KeyboardLine
          key={rowIndex}
          className={rowIndex === 1 ? "max-w-[95%] mx-auto w-full" : undefined}
        >
          {row.map((key, keyIndex) => (
            <React.Fragment key={keyIndex}>
              {renderKey(key)}
            </React.Fragment>
          ))}
        </KeyboardLine>
      ))}
    </div>
  );
});

function getBackgroundColor() {
  return "bg-[#1a1a1a] border border-[2px] border-[#292929]";
}